// Copyright ©️ 2026 eVolve MEP, LLC

import './attachments.css';

import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  type EntityAttachment,
  type EntityAttachmentId,
  type EntityAttachmentType,
  type UploadableEntityAttachmentType,
  type FileId,
  type EvolveApiRoute,
  getEntityAttachmentRoute,
  isUploadableEntityAttachmentType,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { Flex, Text, Loader } from '@mantine/core';

import { AttachmentCard, FileCard } from 'components/Mantine/Attachments/AttachmentCard';
import { DeleteAttachmentModal } from 'components/Mantine/Attachments/DeleteAttachmentModal';
import { SectionFileUploads } from 'components/Mantine/Attachments/SectionFileUploads';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useUniformFlex } from 'hooks/useUniformFlex';

type Props<T extends EntityAttachmentType> = {
  entityType: T;
  entityId: EntityAttachmentId<T>;
  title?: string;
  titleSuffix?: string;
  description?: string;

  /** Called after files have been successfully uploaded. */
  onFilesUploaded?: (fileIds: FileId[]) => void;
  /** Called after a web link has been successfully added. */
  onWebLinkAdded?: () => void;
  /** Called after an attachment has been successfully deleted. */
  onAttachmentDeleted?: () => void;
  /** If true, allows uploads. */
  active?: boolean;
  /** If true, will automatically load the next page of attachments when the user scrolls to the bottom. */
  autoloadRemainingAttachments?: boolean;
  /**
   * Bump this value to force the section to refetch its attachments in place,
   * e.g. when another surface has added/removed attachments for this entity.
   */
  refreshTrigger?: number;

  /**
   * Called when an attachment is selected.
   *
   * If not provided, attachments will automatically download on click.
   */
  onAttachmentSelected?: (attachment: EntityAttachment<never>, rowIndex: number, entityType: T) => void;
  /** Called when the total number of attachments has been loaded. */
  onAttachmentCountRealized?: (payload: { count: number; entityType: T }) => void;

  /**
   * If provided, will be called with data[selectedRow]
   * (or null if that row does not exist in this section)
   */
  onRowRealized?: (row: EntityAttachment<T> | null, rowIndex: number, entityType: T) => void;
  /** Used with `onRowRealized` */
  selectedRow?: number;
  /** For pagination */
  pageSize?: number;
};

const attachmentTypeToTitle = {
  workOrderBop: 'Tasks',
  workOrderItems: 'Work Order Items',
  workOrder: 'Work Order',
  task: 'Task',
  workOrderItem: 'Item',
  taskType: 'Attachments',
  shipment: 'Shipping Order',
} as const satisfies {
  [K in EntityAttachmentType]: string;
};

const attachmentUrl = {
  workOrder: (workOrderId) => getEntityAttachmentRoute('workOrder', workOrderId, 'attachment'),
  task: (taskId) => getEntityAttachmentRoute('task', taskId, 'attachment'),
  taskType: (taskTypeId) => getEntityAttachmentRoute('taskType', taskTypeId, 'attachment'),
  workOrderItem: (workOrderItemId) => getEntityAttachmentRoute('workOrderItem', workOrderItemId, 'attachment'),
  shipment: (shipmentId) => getEntityAttachmentRoute('shipment', shipmentId, 'attachment'),
  // roll-up endpoints
  workOrderBop: (workOrderId) => `shop/workOrder/${workOrderId}/bop/attachment`,
  workOrderItems: (workOrderId) => `shop/workOrder/${workOrderId}/item/attachment`,
} as const satisfies {
  [K in EntityAttachmentType]: (id: EntityAttachmentId<K>) => EvolveApiRoute<'GET_PAGINATED'>;
};
const getAttachmentUrl = <T extends EntityAttachmentType>(entity: T, id: EntityAttachmentId<T>) =>
  (attachmentUrl[entity] as (id: EntityAttachmentId<T>) => EvolveApiRoute<'GET_PAGINATED'>)(id) as ReturnType<
    (typeof attachmentUrl)[T]
  >;

/** Used to render and upload attachments for a particular entity (Work Order, Task, etc.) */
export const AttachmentSection = <T extends EntityAttachmentType>({
  selectedRow,
  onRowRealized,
  onAttachmentCountRealized,
  onAttachmentSelected,
  active,
  entityId,
  entityType,
  title = attachmentTypeToTitle[entityType],
  titleSuffix,
  onFilesUploaded: onFilesUploadedParent,
  onWebLinkAdded: onWebLinkAddedParent,
  onAttachmentDeleted,
  autoloadRemainingAttachments,
  pageSize,
  refreshTrigger,
  ...props
}: Props<T>) => {
  const [deletingAttachment, setDeletingAttachment] = useState<EntityAttachment<T>>();

  const {
    data: initialData,
    fetchNextPage,
    loading,
    reset,
    entireCount,
  } = useEvolvePaginatedGet(
    getAttachmentUrl(entityType, entityId),
    // @ts-expect-error Generics out to get me again
    { perPage: pageSize ?? (active ? 6 : 3) },
  );

  const [data, setData] = useState<typeof initialData>(initialData);
  useEffect(() => setData(initialData), [initialData]);
  useEffect(() => {
    // Fetch the first page of results automatically.
    // Subsequent pages are loaded on demand by the user
    void fetchNextPage();
  }, [fetchNextPage]);

  const lastRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    // Refetch in place when an external surface signals this section's data changed
    if (lastRefreshTrigger.current !== refreshTrigger) {
      lastRefreshTrigger.current = refreshTrigger;
      reset();
    }
  }, [refreshTrigger, reset]);

  const { description = active && data.length > 0 ? 'Drag and drop or click + to add attachments.' : undefined } =
    props;

  useEffect(() => {
    if (isNotNil(entireCount)) onAttachmentCountRealized?.({ count: entireCount, entityType });
  }, [entireCount, entityType, onAttachmentCountRealized]);

  // If `onRowRealized` & `selectedRow` are passed in,
  // this hook will find that row in our `data` and call `onRowRealized`.
  // If the `selectedRow` is beyond the current data length, fetch the next page.
  // If we were unable to find that row even after pulling all the data,
  // call `onRowRealized` with null so the parent can handle the "no data" scenario.
  useEffect(() => {
    (async () => {
      if (isNil(selectedRow) || isNil(onRowRealized) || entireCount === undefined) return;
      const rowToReturn = selectedRow === -1 ? data.length - 1 : selectedRow;
      if (rowToReturn >= data.length && entireCount > data.length) {
        await fetchNextPage();
        return;
      }
      const row = data[rowToReturn] as EntityAttachment<T> | undefined;
      onRowRealized(row ?? null, row ? rowToReturn : selectedRow, entityType);
    })();
  }, [data, entireCount, entityType, fetchNextPage, onRowRealized, selectedRow]);

  const { containerProps, itemProps } = useUniformFlex(300, 450);

  const onFilesUploaded = useCallback<NonNullable<typeof onFilesUploadedParent>>(
    (...params) => {
      reset();
      onFilesUploadedParent?.(...params);
    },
    [reset, onFilesUploadedParent],
  );

  const onWebLinkAdded = useCallback<NonNullable<typeof onWebLinkAddedParent>>(
    (...params) => {
      reset();
      onWebLinkAddedParent?.(...params);
    },
    [reset, onWebLinkAddedParent],
  );

  return (
    <>
      <SectionFileUploads
        entityType={entityType}
        entityId={entityId}
        label={`${title} ${titleSuffix ?? ''}`}
        description={description}
        showUploadBox={entireCount === 0 && active}
        disabled={!active}
        onFilesUploaded={onFilesUploaded}
        onWebLinkAdded={onWebLinkAdded}
      >
        <Flex {...containerProps} gap="xs">
          {data.map((d, index) => (
            <AttachmentCard
              key={d.rowId}
              attachment={d as EntityAttachment<T>}
              entityType={entityType}
              onDeleteAttachment={active ? setDeletingAttachment : null}
              rowIndex={index}
              onAttachmentSelected={onAttachmentSelected}
              selected={index === selectedRow}
              {...itemProps}
            />
          ))}

          {entireCount === 0 && !active && (
            <FileCard icon={null} py={4} mih={0} {...itemProps}>
              <Text c="dimmed" fz="xs">
                No attachments.
              </Text>
            </FileCard>
          )}
        </Flex>
        {autoloadRemainingAttachments ? (
          <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
        ) : loading ? (
          <Loader type="dots" />
        ) : (
          !!entireCount &&
          data.length < entireCount && (
            <Flex align="center" fz="xs">
              <Text onClick={() => fetchNextPage()} className="link">
                load more ({entireCount - data.length} remaining)
              </Text>
            </Flex>
          )
        )}
      </SectionFileUploads>

      {isUploadableEntityAttachmentType(entityType) && (
        <DeleteAttachmentModal
          attachment={deletingAttachment as EntityAttachment<T & UploadableEntityAttachmentType> | undefined}
          entityType={entityType}
          refresh={reset}
          onAttachmentDeleted={onAttachmentDeleted}
          onClose={() => setDeletingAttachment(undefined)}
        />
      )}
    </>
  );
};
